import { RedirectToSignIn } from '@daveyplate/better-auth-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLoaderData } from '@tanstack/react-router';
import { ro } from 'date-fns/locale';
import { AlertCircle, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Footer } from '@/components/Footer';
import {
  MultiSelectOption,
  MultiSelectPopover,
} from '@/components/MultiSelectPopover';
import { Navbar } from '@/components/Navbar';
import { NewsCard } from '@/components/NewsCard';
import { SelectedChips } from '@/components/SelectedChips';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFilters } from '@/contexts/FilterContext';
import { useSession } from '@/hooks/authHooks';
import { useSubscription } from '@/hooks/useSubscription';
import { useSymbols } from '@/hooks/useSymbols';
import {
  useCreateWatcher,
  useDeleteWatcher,
  useUpdateWatcher,
  useWatchers,
} from '@/hooks/useWatchers';
import { track } from '@/lib/mixpanel';
import { apiRequest } from '@/lib/queryClient';
import { ArticleSource, getDisplaySourceName, NewsArticle } from '@/schema';
import type { Watcher } from '@/types';

const DATA_SOURCE_OPTIONS = [
  { value: 'financial_news', label: 'Știri financiare' },
  { value: 'financial_reports', label: 'Rapoarte financiare' },
] as const;

const alertSchema = z.object({
  prompt: z.string().min(1, 'Promptul este obligatoriu'),
  active: z.boolean(),
  data_source: z.enum(['financial_news', 'financial_reports']),
});

type AlertFormValues = z.infer<typeof alertSchema>;

function PromptInput({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor="prompt" className="text-sm font-medium text-neutral-700">
        Prompt personalizat
      </label>
      <Textarea
        id="prompt"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Descrie ce tip de știri te interesează..."
        className="min-h-[100px] resize-none"
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}

function FilterSection({ dataSource }: { dataSource: 'financial_news' | 'financial_reports' }) {
  const {
    selectedSymbols,
    setSelectedSymbols,
    selectedSources,
    setSelectedSources,
  } = useFilters();

  const { data: symbolsData } = useSymbols();

  const handleSymbolSelect = (symbol: string) => {
    const isAdding = !selectedSymbols.includes(symbol);
    if (selectedSymbols.includes(symbol)) {
      setSelectedSymbols(selectedSymbols.filter((s) => s !== symbol));
    } else {
      setSelectedSymbols([...selectedSymbols, symbol]);
    }
    track('CreateAlert_SymbolToggled', {
      symbol,
      action: isAdding ? 'add' : 'remove',
    });
  };

  const removeSymbol = (symbolToRemove: string) => {
    setSelectedSymbols(selectedSymbols.filter((s) => s !== symbolToRemove));
  };

  const handleSourceSelect = (source: ArticleSource) => {
    const isAdding = !selectedSources.includes(source);
    if (selectedSources.includes(source)) {
      setSelectedSources(selectedSources.filter((s) => s !== source));
    } else {
      setSelectedSources([...selectedSources, source]);
    }
    track('CreateAlert_SourceToggled', {
      source,
      action: isAdding ? 'add' : 'remove',
    });
  };

  const removeSource = (sourceToRemove: string) => {
    setSelectedSources(selectedSources.filter((s) => s !== sourceToRemove));
  };

  const predefinedSources = Object.values(ArticleSource) as ArticleSource[];

  const symbolValues = Array.isArray(symbolsData?.symbols)
    ? (symbolsData.symbols as string[])
    : [];

  const symbolOptions: MultiSelectOption[] = symbolValues.map((symbol) => ({
    value: symbol,
    label: symbol,
    isSelected: selectedSymbols.includes(symbol),
  }));

  const sourceOptions: MultiSelectOption[] = predefinedSources.map(
    (source) => ({
      value: source,
      label: getDisplaySourceName(source),
      isSelected: selectedSources.includes(source),
    })
  );

  const sourceChips = selectedSources.map((source) => {
    const label = getDisplaySourceName(source as ArticleSource);
    return {
      value: source,
      label,
      removeLabel: `Elimină sursa ${label}`,
    };
  });

  const symbolChips = selectedSymbols.map((symbol) => ({
    value: symbol,
    label: symbol,
    removeLabel: `Elimină simbolul ${symbol}`,
  }));

  const formatSelectionSummary = (
    count: number,
    defaultLabel: string
  ): string => {
    if (count === 0) return defaultLabel;
    if (count === 1) return `${count} selectat`;
    return `${count} selectate`;
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      {dataSource === 'financial_news' && (
        <div className="flex flex-col gap-2 sm:w-64">
          <MultiSelectPopover
            summary={formatSelectionSummary(
              selectedSources.length,
              'Selectează surse...'
            )}
            options={sourceOptions}
            onSelect={(value) => handleSourceSelect(value as ArticleSource)}
            searchPlaceholder="Caută sursă..."
            emptyMessage="Nicio sursă găsită."
          />
          <SelectedChips items={sourceChips} onRemove={removeSource} />
        </div>
      )}

      <div className="flex flex-col gap-2 sm:w-64">
        <MultiSelectPopover
          summary={formatSelectionSummary(
            selectedSymbols.length,
            'Selectează simboluri...'
          )}
          options={symbolOptions}
          onSelect={handleSymbolSelect}
          searchPlaceholder="Caută simbol..."
          emptyMessage="Niciun simbol găsit."
        />
        <SelectedChips items={symbolChips} onRemove={removeSymbol} />
      </div>
    </div>
  );
}

const getDefaultPreviewDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7); // 7 days ago
  return date;
};

function WatcherListItem({
  watcher,
  onEdit,
  onDelete,
}: {
  watcher: Watcher;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const symbolCount = watcher.preferences?.symbols?.length || 0;
  const sourceCount = watcher.preferences?.sources?.length || 0;
  const dataSourceLabel = watcher.data_source === 'financial_reports'
    ? 'Rapoarte financiare'
    : 'Știri financiare';

  return (
    <div className="flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-neutral-800">
          {watcher.prompt || 'Fără prompt'}
        </p>
        <p className="mt-1 text-xs text-neutral-600">
          <span className="rounded bg-neutral-200 px-1.5 py-0.5">{dataSourceLabel}</span>
          {' · '}
          {symbolCount} {symbolCount === 1 ? 'simbol' : 'simboluri'},{' '}
          {sourceCount} {sourceCount === 1 ? 'sursă' : 'surse'}
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          {watcher.active ? (
            <span className="font-medium text-green-primary">Activă</span>
          ) : (
            <span className="text-neutral-500">Inactivă</span>
          )}
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-neutral-600 hover:text-neutral-800">
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-red-600 hover:text-red-800">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function CreateAlertPage() {
  const { session, user, isPending } = useSession();
  const isAuthenticated = Boolean(session ?? user);

  const initialWatchers = useLoaderData({
    from: '/create-alert',
  });
  const { data: watchersData } = useWatchers({
    enabled: isAuthenticated && !isPending,
  });
  const watchers = watchersData || initialWatchers || [];

  // Get subscription info for alert limits
  const { alertLimit, canCreateAlert, tier } = useSubscription({
    enabled: isAuthenticated && !isPending,
  });

  const {
    selectedSymbols,
    selectedSources,
    setSelectedSymbols,
    setSelectedSources,
  } = useFilters();
  const [previewDate, setPreviewDate] = useState<Date | undefined>(
    getDefaultPreviewDate()
  );
  const [previewResults, setPreviewResults] = useState<NewsArticle[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [hasAttemptedPreview, setHasAttemptedPreview] = useState(false);
  const [editingWatcherId, setEditingWatcherId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const createMutation = useCreateWatcher();
  const updateMutation = useUpdateWatcher();
  const deleteMutation = useDeleteWatcher();

  // Find the watcher being edited
  const editingWatcher = editingWatcherId
    ? watchers.find((w: Watcher) => w.watcher_id === editingWatcherId)
    : null;

  const form = useForm<AlertFormValues>({
    resolver: zodResolver(alertSchema),
    defaultValues: {
      prompt: editingWatcher?.prompt || '',
      active: editingWatcher?.active ?? true,
      data_source: editingWatcher?.data_source || 'financial_news',
    },
  });

  const watchedDataSource = form.watch('data_source');

  // Update form when editing watcher changes
  React.useEffect(() => {
    if (editingWatcher) {
      form.reset({
        prompt: editingWatcher.prompt || '',
        active: editingWatcher.active,
        data_source: editingWatcher.data_source || 'financial_news',
      });
      setSelectedSymbols(editingWatcher.preferences?.symbols || []);
      setSelectedSources(
        (editingWatcher.preferences?.sources || []) as ArticleSource[]
      );
    }
  }, [editingWatcher, form, setSelectedSymbols, setSelectedSources]);

  const handlePreview = async () => {
    const prompt = form.getValues('prompt');
    if (!prompt.trim()) {
      form.setError('prompt', {
        type: 'manual',
        message: 'Promptul este obligatoriu pentru previzualizare',
      });
      return;
    }

    if (!previewDate) {
      toast.error('Eroare', {
        description: 'Selectați o dată pentru previzualizare.',
      });
      return;
    }

    track('CreateAlert_PreviewClicked', {
      symbolCount: selectedSymbols.length,
      sourceCount: selectedSources.length,
    });

    setIsPreviewLoading(true);
    try {
      // Convert single date to day start and end timestamps
      const dayStart = new Date(previewDate);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(previewDate);
      dayEnd.setHours(23, 59, 59, 999);

      const payload = {
        prompt,
        dateRange: {
          from: dayStart.getTime(),
          to: dayEnd.getTime(),
        },
        ...(selectedSymbols.length > 0 && { symbols: selectedSymbols }),
        ...(selectedSources.length > 0 && { sources: selectedSources }),
      };

      const response = await apiRequest('POST', '/api/news/preview', payload);
      const data = await response.json();
      setPreviewResults(data.news || []);
      setHasAttemptedPreview(true);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Eroare', {
        description: 'Nu s-au putut prelua rezultatele previzualizării.',
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSave = async (values: AlertFormValues) => {
    try {
      const payload = {
        prompt: values.prompt,
        active: values.active,
        data_source: values.data_source,
        preferences: {
          symbols: selectedSymbols,
          sources: values.data_source === 'financial_news' ? selectedSources : [],
        },
      };

      if (editingWatcherId) {
        // Update existing watcher
        await updateMutation.mutateAsync({
          watcherId: editingWatcherId,
          payload,
        });
        toast.success('Succes!', {
          description: 'Alerta AI a fost actualizată cu succes.',
        });
        setEditingWatcherId(null);
      } else {
        // Create new watcher
        await createMutation.mutateAsync(payload);
        toast.success('Succes!', {
          description: 'Alerta AI a fost creată cu succes.',
        });
        setIsCreatingNew(false);
      }

      // Reset form
      form.reset({ prompt: '', active: true, data_source: 'financial_news' });
      setSelectedSymbols([]);
      setSelectedSources([]);

      // Scroll back to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });

      track('WatcherSaved', {
        isNew: !editingWatcherId,
        hasSymbols: selectedSymbols.length > 0,
        hasSources: selectedSources.length > 0,
      });
    } catch (error) {
      console.error('Error saving watcher:', error);
      const errorMessage =
        error instanceof Error && error.message === 'WATCHER_LIMIT_REACHED'
          ? 'Ai atins limita de 5 alerte. Șterge o alertă existentă pentru a crea una nouă.'
          : 'Salvarea alertei a eșuat. Te rugăm să încerci din nou.';
      toast.error('Eroare', {
        description: errorMessage,
      });
    }
  };

  const handleEdit = (watcher: Watcher) => {
    track('CreateAlert_EditClicked', { watcherId: watcher.watcher_id });
    setEditingWatcherId(watcher.watcher_id);
    setIsCreatingNew(false);
    // Scroll to form after state update with offset for navbar
    setTimeout(() => {
      const element = formRef.current;
      if (element) {
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 70;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleDelete = async (watcherId: string) => {
    if (!confirm('Sigur dorești să ștergi această alertă?')) {
      return;
    }

    track('CreateAlert_DeleteClicked', { watcherId });

    try {
      await deleteMutation.mutateAsync(watcherId);
      toast.success('Alertă ștearsă', {
        description: 'Alerta AI a fost ștearsă cu succes.',
      });
      if (editingWatcherId === watcherId) {
        setEditingWatcherId(null);
      }
      track('CreateAlert_DeleteSuccess', { watcherId });
    } catch (error) {
      console.error('Error deleting watcher:', error);
      toast.error('Eroare', {
        description: 'Ștergerea alertei a eșuat. Te rugăm să încerci din nou.',
      });
      track('CreateAlert_DeleteFailed', { watcherId });
    }
  };

  const handleCancel = () => {
    track('CreateAlert_CancelClicked', { isEditing: !!editingWatcherId });
    setEditingWatcherId(null);
    setIsCreatingNew(false);
    form.reset({ prompt: '', active: true, data_source: 'financial_news' });
    setSelectedSymbols([]);
    setSelectedSources([]);
  };

  const handleStartCreate = () => {
    if (!canCreateAlert) {
      toast.error('Limită atinsă', {
        description: `Ai atins limita de ${alertLimit} alerte. Șterge o alertă existentă sau fă upgrade pentru a crea mai multe.`,
      });
      return;
    }
    track('CreateAlert_CreateNewClicked');
    setIsCreatingNew(true);
    setEditingWatcherId(null);
    form.reset({ prompt: '', active: true, data_source: 'financial_news' });
    setSelectedSymbols([]);
    setSelectedSources([]);
    // Scroll to form after state update with offset for navbar
    setTimeout(() => {
      const element = formRef.current;
      if (element) {
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 70;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const showForm = isCreatingNew || editingWatcherId !== null;

  if (isPending) {
    return null;
  }

  if (!isAuthenticated) {
    return <RedirectToSignIn />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-800">
      <Navbar />
      <div className="mx-auto w-full max-w-5xl flex-1 px-4 pb-8 pt-28">
        <h1 className="mb-2 font-heading text-2xl font-bold">Alerte AI</h1>
        <p className="mb-8 text-neutral-600">
          Configurează un prompt personalizat și selectează filtrele dorite.
          Poți previzualiza rezultatele înainte de a salva alerta.
        </p>

        {/* Free Plan Upgrade Banner */}
        {tier === 'free' && (
          <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-6 w-6 flex-shrink-0 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-900">
                  Ești pe planul gratuit
                </h3>
                <div className="mt-1 w-full text-sm text-amber-800">
                  Ai inclusă doar o alertă și 10 notificări. Pentru mai multe
                  alerte și notificări nelimitate, fă upgrade.
                </div>
                <Link
                  to="/upgrade"
                  className="mt-2 inline-flex items-center text-sm font-medium text-amber-900 hover:text-amber-700">
                  Vezi planurile <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Watcher List */}
        <div className="mb-8 rounded-2xl border border-neutral-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-neutral-800">
              Alertele tale ({watchers.length}/{alertLimit})
            </h2>
            <Button
              onClick={handleStartCreate}
              disabled={!canCreateAlert || isCreatingNew}>
              Crează alertă nouă
            </Button>
          </div>

          {watchers.length === 0 ? (
            <p className="text-neutral-500">
              Nu ai încă nicio alertă. Creează una pentru a începe!
            </p>
          ) : (
            <div className="space-y-3">
              {watchers.map((watcher: Watcher) => (
                <WatcherListItem
                  key={watcher.watcher_id}
                  watcher={watcher}
                  onEdit={() => handleEdit(watcher)}
                  onDelete={() => handleDelete(watcher.watcher_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Edit/Create Form */}
        {showForm && (
          <div
            ref={formRef}
            className="mb-8 rounded-2xl border-2 border-green-primary bg-white p-6">
            <h2 className="mb-6 text-xl font-semibold text-neutral-800">
              {editingWatcherId ? 'Editează Alerta' : 'Crează o alertă nouă'}
            </h2>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="space-y-6">
                <FormField
                  control={form.control}
                  name="data_source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-neutral-700">
                        Tip de monitorizare
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="flex gap-4">
                          {DATA_SOURCE_OPTIONS.map((option) => (
                            <label
                              key={option.value}
                              className="flex cursor-pointer items-center gap-2">
                              <RadioGroupItem value={option.value} />
                              <span className="text-sm text-neutral-700">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prompt"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <PromptInput
                          value={field.value}
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FilterSection dataSource={watchedDataSource} />

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          id="active"
                        />
                      </FormControl>
                      <FormLabel
                        htmlFor="active"
                        className="!mt-0 cursor-pointer">
                        Alertă activă
                      </FormLabel>
                    </FormItem>
                  )}
                />

                <div className="flex flex-wrap gap-3">
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                    className="bg-green-primary hover:bg-green-primary/90">
                    Salvează
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}>
                    Anulează
                  </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-block">
                          <Button
                            type="button"
                            onClick={handlePreview}
                            disabled={isPreviewLoading || !form.getValues('prompt') || watchedDataSource === 'financial_reports'}
                            className="bg-neutral-900 hover:bg-neutral-800">
                            {isPreviewLoading ? 'Se încarcă...' : 'Previzualizează'}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {watchedDataSource === 'financial_reports' && (
                        <TooltipContent>
                          <p>Previzualizarea nu este disponibilă pentru rapoarte financiare</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Date selection as inline link */}
                <div className="mt-4">
                  <p className="w-full min-w-full text-sm text-neutral-600">
                    Previzualizarea se va face pe o singură zi (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="font-medium text-neutral-800 underline hover:text-neutral-900">
                          {previewDate
                            ? previewDate.toLocaleDateString('ro-RO', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })
                            : new Date(
                                Date.now() - 7 * 24 * 60 * 60 * 1000
                              ).toLocaleDateString('ro-RO', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={previewDate}
                          onSelect={setPreviewDate}
                          locale={ro}
                          disabled={(date) => date > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    ). Dacă doriți să schimbați ziua apăsați{' '}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="font-medium text-neutral-800 underline hover:text-neutral-900">
                          aici
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={previewDate}
                          onSelect={setPreviewDate}
                          locale={ro}
                          disabled={(date) => date > new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    .
                  </p>
                </div>
              </form>
            </Form>
          </div>
        )}

        {/* Preview Results Section */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <h2 className="mb-4 text-xl font-semibold text-neutral-800">
            Previzualizare
          </h2>

          {!hasAttemptedPreview ? (
            <p className="w-full min-w-full py-8 text-left text-neutral-500">
              Apasă butonul "Previzualizează" din alertă pentru a vedea ce
              articole se potrivesc cu criteriile tale.
            </p>
          ) : previewResults.length === 0 ? (
            <p className="py-8 text-center text-neutral-500">
              Nu s-au găsit articole pentru această configurație.
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600">
                {previewResults.length}{' '}
                {previewResults.length === 1
                  ? 'articol găsit'
                  : 'articole găsite'}
              </p>
              {previewResults.map((article: NewsArticle) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
